import { EventEmitter, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Event, NavigationEnd, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { RightPanelService } from 'src/app/components/rightpanel/rightpanel.service';
import { SSIService } from '../+products/ssi/ssi.service';

import { CommunityProgramsService } from '../+products/community/community-programs.service';
import { LISService } from '../+products/lis/lis.service';
import { MedicaidService } from '../+products/medicaid/medicaid.service';
import { SNAPService } from '../+products/snap/snap.service';
import { VeteranService } from '../+products/veteran/veteran.service';
import { Member } from '../../models/member';
import { Role } from '../../models/role';
import { PermissionType, User } from '../../models/user';
import { FeatureService } from '../../shared/preview-feature/preview-feature.service';
import { MemberService } from '../member/member.service';
import { TenantService } from '../tenant/tenant.service';
import { PermissionDictionary, UserPermissionService } from '../user/user-permission.service';
import { UserService } from '../user/user.service';
import { WorkItemService } from '../workitems/work-item.service';
import { CustomMenuItem, ICustomMenuItem, IMenuWrapper } from './menu.models';

@Injectable({
    providedIn: 'root'
})
export class MenuService {

    private _currentMenu$ = new BehaviorSubject<IMenuWrapper>(null);
    private currentMenu$ = this._currentMenu$.asObservable();

    @Select(UserService.user)
    user$: Observable<User>;

    @Select(MemberService.member)
    member$: Observable<Member>;
    menu$: Observable<IMenuWrapper>;
    currentRole$: Observable<Role>;

    menus: IMenuWrapper[] = [
        {
            name: 'member',
            items: (() => {
                const result = this.generateMemberMenuItems();
                return result;
            }).bind(this)
        },
        {
            name: 'admin',
            items: (() => {
                const result = this.generateAdminMenuItems();
                return result;
            }).bind(this)
        },
        {
            name: 'tenant',
            items: (() => {
                const result = this.generateTenantMenuItems();
                return result;
            }).bind(this)
        },
        {
            name: 'government_relations',
            items: (() => {
                const result = this.generateGovernmentRelationsMenuItems();
                return result;
            }).bind(this)
        },
        {
            name: null,
            items: (() => {
                const result = this.generateGeneralMenuItems();
                return result;
            }).bind(this)
        }
    ];

    navigationEvent$ = new BehaviorSubject<NavigationEnd>(null);

    forceMenuGeneration$ = new EventEmitter<any>();

    routeEnd$$: any;

    constructor(
        private store: Store,
        public router: Router,
        public route: ActivatedRoute,
        private memberService: MemberService,
        private communityService: CommunityProgramsService,
        private veteranService: VeteranService,
        private medicaidService: MedicaidService,
        private snapService: SNAPService,
        private lisService: LISService,
        private ssiService: SSIService,
        private rightPanelService: RightPanelService,
        private featureService: FeatureService,
        private workItemService: WorkItemService,
        public permissionService: UserPermissionService,
        private tenantService: TenantService
    ) {

        permissionService.registerPermissions([
            { selectionName: PermissionDictionary.Cli, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.Configurations, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.Users, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.Orchestrations, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.Templates, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.DocumentTypes, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.Tenants, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.GR, permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.Facilities, permissionType: PermissionType.permission },
            { selectionName: "UI.Member.GovernmentRelations", permissionType: PermissionType.permission },
            { selectionName: PermissionDictionary.ProcessView, permissionType: PermissionType.namespace }
        ]);

        this.currentRole$ = this.store.select<Role>(UserService.currentRole);

        combineLatest([this.navigationEvent$, this.forceMenuGeneration$.pipe(startWith(true)), this.currentRole$]).subscribe(([navigationEvent, _]) => {
            const routeSnapshot = this.firstChild(this.route.snapshot);
            const menuName = this.getMenuName(routeSnapshot);

            // If the menu is already set and the menu name is the same as the current menu name, return the current menu
            if (!this._currentMenu$?.value || menuName != this._currentMenu$?.value?.name) {
                let menu = menuName && this.menus.find(item => item.name === menuName);

                if (!menu) {
                    menu = this.menus.find(item => item.name === null);
                }

                if (menu) {
                    this._currentMenu$.next(menu);
                }
            }
        });

        this.initializeRouteListener();
    }

    getCurrentMenuAsync() {
        return this.currentMenu$;
    }


    getMenuName(routeSnapshot: ActivatedRouteSnapshot) {
        let result = routeSnapshot && routeSnapshot.data && routeSnapshot.data.menu as string;
        if (result === undefined && routeSnapshot.parent) {
            result = this.getMenuName(routeSnapshot.parent);
        }

        return result;
    }

    firstChild(routeSnapshot: ActivatedRouteSnapshot) {
        const result = (routeSnapshot && this.firstChild(routeSnapshot.firstChild)) || routeSnapshot;
        return result;
    }

    initializeRouteListener() {
        this.routeEnd$$ = this.router.events.subscribe((event: Event) => {
            if (event instanceof NavigationEnd) {
                this.navigationEvent$.next(event);
            }
        });
    }


    generateGeneralMenuItems() {

        return combineLatest([this.user$, this.currentRole$, this.permissionService.permissionsChanged$])
            .pipe(switchMap(([userInfo, currentRole]) => {

                const processViewAccessPerms = this.permissionService.getPermissionsByNameSpace(PermissionDictionary.ProcessViewAccess);

                let result: ICustomMenuItem[] = [];

                result.push({
                        order: 10, label: 'Dashboard', icon: 'fal fa-toolbox', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/dashboard'],
                        routerLinkActiveOptions: { exact: true }
                    });



                result.push({
                    order: 11,
                    routerLink: ['/messages'],
                    routerLinkActiveOptions: { exact: false },
                    component: LazyLoadedWidgets.MEMBER_NOTIFICATION_MESSAGES,
                });

                result.push({
                    order: 12,

                    routerLink: ['/myworkitems'],
                    routerLinkActiveOptions: { exact: false },
                    component: LazyLoadedWidgets.USER_WORK_ITEMS,
                });


                result.push({
                    order: 13, isSeparator: true
                });
                result.push({
                    order: 14,
                    component: LazyLoadedWidgets.REPORTS_MENU,
                    state: { scope: "MainMenu" }
                });


                result.push({
                    order: 15, isSeparator: true
                });

                if (currentRole?.inRole('Admin') || processViewAccessPerms.length > 0) {
                    result.push({
                        order: 16,
                        label: 'View Manager',
                        icon: 'fal fa-filter-list',
                        iconType: 'fontawesome',
                        iconSize: '18px',
                        routerLink: ['/profile-manager'],
                        routerLinkActiveOptions: { exact: false },
                    });
                }

                result.push({
                    order: 17,

                    routerLink: ['/userviews'],
                    routerLinkActiveOptions: { exact: false },
                    component: LazyLoadedWidgets.USER_VIEWS,
                });

                if (currentRole) {
                    result.push({
                        order: 18,
                        component: LazyLoadedWidgets.PROCESS_VIEW_ITEMS,
                    });
                }


                result.push({ order: 49, isSeparator: true });

                if ((currentRole?.inRole('Admin')) || this.permissionService.hasPermission(PermissionDictionary.Users) == true) {
                    result.push({
                        order: 55, label: 'Users', icon: 'fal fa-users', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/user-manager'],
                        routerLinkActiveOptions: { exact: false }
                    });
                }

                if (this.permissionService.hasPermission(PermissionDictionary.Configurations) == true) {
                    result.push({
                        order: 56, label: 'Configurations', icon: 'fal fa-sliders', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/configs'],
                        routerLinkActiveOptions: { exact: true }
                    });
                }

                if (this.permissionService.hasPermission(PermissionDictionary.Templates) == true) {
                    result.push({
                        order: 60, label: 'Templates', icon: 'fal fa-project-diagram', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/templates'],
                        routerLinkActiveOptions: { exact: false }
                    });
                }

                if (currentRole?.inRole('Admin')) {
                    result.push({
                        order: 63, label: 'Document Types', icon: 'fal fa-files', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/document-type'],
                        routerLinkActiveOptions: { exact: false },
                    });
                }

                if (this.permissionService.hasPermission(PermissionDictionary.Orchestrations) == true) {
                    result.push({
                        order: 70, label: 'Campaigns', icon: 'fal fa-route', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/orchestration'],
                        routerLinkActiveOptions: { exact: false }
                    });
                }

                if (this.permissionService.hasPermission(PermissionDictionary.Tenants)) {
                    result.push({
                        order: 80, label: 'Tenants', icon: 'fal fa-buildings', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/tenant'],
                        routerLinkActiveOptions: { exact: false }
                    });
                }

                if (this.permissionService.hasPermission(PermissionDictionary.GR)) {
                    result.push({
                        order: 81, label: 'Government Relations', icon: 'fal fa-building-columns', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/government-relations'],
                        routerLinkActiveOptions: { exact: true }
                    });
                }

                result.push({
                    order: 95, isSeparator: true
                });


                if (this.permissionService.hasPermission(PermissionDictionary.Facilities)) {
                    result.push({
                        order: 96, label: 'Facility Requests', icon: 'fal fa-hospital-alt', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/facility/request'],
                        routerLinkActiveOptions: { exact: true }
                    });
                }


                result.push({
                    order: 97, label: 'Search', icon: 'fal fa-search', iconType: 'fontawesome', iconSize: '18px',
                    routerLink: ['/search'],
                    routerLinkActiveOptions: { exact: true }
                });


                if (this.permissionService.hasPermission(PermissionDictionary.Cli)) {
                    result.push({
                        order: 100, label: 'Admin', icon: 'fal fa-link-horizontal', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: ['/admin'],
                        routerLinkActiveOptions: { exact: true }
                    });
                }

                result = CustomMenuItem.orderMenuItems(result);

                return of(result);
            }));
    }

    generateMemberMenuItems() {
        const communityAvailability$ = this.communityService.status$;
        const medicaidAvailability$ = this.medicaidService.status$;
        const lisAvailability$ = this.lisService.status$;
        const snapAvailability$ = this.snapService.status$;
        const veteranAvailability$ = this.veteranService.status$;
        const ssiAvailability$ = this.ssiService.status$;

        return combineLatest(this.member$, this.user$, this.currentRole$, communityAvailability$, medicaidAvailability$, snapAvailability$, lisAvailability$, veteranAvailability$, ssiAvailability$)
            .pipe(map(([member, userInfo, currentRole, communityAvailability, medicaidAvailability, snapAvailability, lisAvailability, veteranAvailability, ssiAvailability]) => ({ member, userInfo, currentRole, communityAvailability, medicaidAvailability, snapAvailability, lisAvailability, veteranAvailability, ssiAvailability })))
            .pipe(switchMap(bundle => {


                let result: ICustomMenuItem[] = [];

                result.push({
                    order: 0, label: 'Home', icon: 'fal fa-home', iconType: 'fontawesome', iconSize: '18px',
                    routerLink: ['/'],
                    routerLinkActiveOptions: { exact: true }
                });

                if (this.memberService.base64Sync) {
                    result.push({
                        order: 2, label: 'Dashboard', icon: 'fal fa-toolbox', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/dashboard`],
                        routerLinkActiveOptions: { exact: true },
                    });
                }

                result.push({
                    order: 9, isSeparator: true
                });

                if (this.memberService.base64Sync && bundle.communityAvailability) {
                    result.push({
                        order: 11,
                        routerLink: [`/member/${this.memberService.base64Sync}/community`],
                        component: LazyLoadedWidgets.COMMUNITYLYNK_MENU
                    });
                }

                if(bundle.medicaidAvailability) {
                    result.push({
                        order: 12, label: this.medicaidService.categoryDisplayNameSync, icon: this.medicaidService.iconSync, iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/medicaid`],
                        routerLinkActiveOptions: undefined,
                        disabled: !bundle.medicaidAvailability.active
                    });
                }

                if(bundle.snapAvailability) {
                    result.push({
                        order: 13, label: this.snapService.categoryDisplayNameSync, icon: this.snapService.iconSync, iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/snap`],
                        routerLinkActiveOptions: undefined,
                        disabled: !bundle.snapAvailability.active
                    });
                }

                if(bundle.lisAvailability) {
                    result.push({
                        order: 14, label: this.lisService.categoryDisplayNameSync, icon: this.lisService.iconSync, iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/lis`],
                        routerLinkActiveOptions: undefined,
                        disabled: !bundle.lisAvailability.active
                    });
                }

                if(bundle.veteranAvailability) {
                    result.push({
                        order: 15, label: this.veteranService.categoryDisplayNameSync, icon: this.veteranService.iconSync, iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/va`],
                        routerLinkActiveOptions: undefined,
                        disabled: !bundle.veteranAvailability.active
                    });
                }

                if(bundle.ssiAvailability) {
                    result.push({
                        order: 15, label: this.ssiService.categoryDisplayNameSync, icon: this.ssiService.iconSync, iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/ssi`],
                        routerLinkActiveOptions: undefined,
                        disabled: !bundle.ssiAvailability.active
                    });
                }

                result.push({
                    order: 20, isSeparator: true
                });

                result.push({
                    order: 21, label: 'Profile', icon: 'fal fa-user', iconType: 'fontawesome', iconSize: '18px',
                    routerLink: [`/member/${this.memberService.base64Sync}/profile`],
                    routerLinkActiveOptions: { exact: true }
                });

                result.push({
                    order: 22, label: 'Notes', icon: 'fal fa-comment-dots', iconType: 'fontawesome', iconSize: '18px',
                    routerLink: [`/member/${this.memberService.base64Sync}/notes`],
                    routerLinkActiveOptions: { exact: true }
                });

                result.push({
                    order: 23, label: 'Documents', icon: 'fal fa-file-alt', iconType: 'fontawesome', iconSize: '18px',
                    routerLink: [`/member/${this.memberService.base64Sync}/files`],
                    routerLinkActiveOptions: { exact: true }
                });

                result.push({
                    order: 24, label: 'Facility Requests', icon: 'fal fa-hospital-alt', iconType: 'fontawesome', iconSize: '18px',
                    routerLink: [`/member/${this.memberService.base64Sync}/facility/request`],
                    routerLinkActiveOptions: { exact: true },
                });

                if (bundle.member) {
                    result.push({
                        order: 25,
                        label: `Relationships`,
                        icon: 'fal fa-users', iconType: 'fontawesome', iconSize: '18px',
                        command: ((event?: any) => {
                            this.rightPanelService.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_RIGHTPANEL_RELATIONS);
                        }).bind(this),
                        disabled: !(bundle.member as Member).relations.length,
                        badgeValue: (bundle.member as Member).relations.length > 0 ? (bundle.member as Member).relations.length : undefined,
                        badgeSeverity: 'danger',
                        badgeStyleClass: 'tw-mr-4'

                    });
                }

                result.push({
                    order: 40, isSeparator: true
                });

                result.push({
                    order: 41, label: 'Search', icon: 'fal fa-search', iconType: 'fontawesome', iconSize: '18px', routerLink: ['/search']
                });

                if (this.memberService.base64Sync) {
                    result.push({
                        order: 3, label: 'Work Items', icon: this.workItemService.getDefaultIcon(), iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/workitem`],
                        routerLinkActiveOptions: { exact: true },
                    });
                }

                if (this.memberService.base64Sync) {
                    result.push({
                        order: 10, label: 'Products', icon: 'fa-light fa-diagram-subtask', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/products`],
                        routerLinkActiveOptions: { exact: true },
                    });
                }

                if (bundle.currentRole?.inRole('Admin')) {
                    result.push({ order: 99, isSeparator: true });
                    result.push({ order: 100, label: 'Admin', iconType: 'fontawesome', icon: 'fal fa-shield-alt', iconSize: '18px', bold: true, routerLink: ['/admin'] });
                }

                if (bundle.currentRole?.inRole('Admin')) {

                    result.push({
                        order: 27, label: 'Properties', icon: 'fa-light fa-key-skeleton-left-right', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/properties`],
                        routerLinkActiveOptions: { exact: true },
                    });
                }

                if (this.permissionService.hasPermission('UI.Member.GovernmentRelations') || (bundle.currentRole?.inRole('Admin'))) {
                    result.push({
                        order: 26, label: 'Government Relations', icon: 'fal fa-building-columns', iconType: 'fontawesome', iconSize: '18px',
                        routerLink: [`/member/${this.memberService.base64Sync}/government-relations`],
                        routerLinkActiveOptions: { exact: true },
                    });
                }

                result = CustomMenuItem.orderMenuItems(result);

                return of(result);
            }));
    }


    generateAdminMenuItems() {
        let result: ICustomMenuItem[] = [
            {
                order: 0, label: 'Home', icon: 'fal fa-home', iconType: 'fontawesome', iconSize: '18px', routerLink: ['/']
            },
        ];

        result = CustomMenuItem.orderMenuItems(result);

        return of(result);
    }

    generateTenantMenuItems() {

        const tenants$ = this.tenantService.allTenants$.pipe(tap(tenants => {
            const tenantPermissions = tenants.map(tenant => `${PermissionDictionary.Tenants}.${tenant.tenantName}`);
            this.permissionService.registerPermissions(tenantPermissions);
        }));

        return combineLatest([tenants$, this.currentRole$, this.permissionService.permissionsChanged$])
            .pipe(filter(([tenants, currentRole]) => !!tenants && !!currentRole))
            .pipe(switchMap(([tenants, currentRole]) => {

                let result: ICustomMenuItem[] = [
                    {
                        order: 0, label: 'Home', icon: 'fal fa-home', iconType: 'fontawesome', iconSize: '18px', routerLink: ['/'], routerLinkActiveOptions: { exact: true }
                    },
                    {
                        order: 1, label: 'Dashboard', icon: 'fal fa-toolbox', iconType: 'fontawesome', iconSize: '18px', routerLink: ['/tenant'], routerLinkActiveOptions: { exact: true }
                    }
                ];


                for (let tenant of tenants) {
                    if (currentRole?.inRole('Admin') || this.permissionService.hasPermission(`${PermissionDictionary.Tenants}.${tenant.tenantName}`)) {
                        result.push({
                            order: 2, label: tenant.displayName, icon: 'fal fa-buildings', iconType: 'fontawesome', iconSize: '18px',
                            routerLink: [`/tenant/${tenant.tenantName}`],
                            routerLinkActiveOptions: { exact: true }
                        });
                    }
                }


                result = CustomMenuItem.orderMenuItems(result);

                return of(result);

            }));
    }


    generateGovernmentRelationsMenuItems() {


        this.permissionService.registerPermissions([PermissionDictionary.GR_OfficeFollowup]);

        return combineLatest([this.user$, this.currentRole$, this.permissionService.permissionsChanged$])
            .pipe(filter(([userInfo, currentRole]) => !!userInfo && !!currentRole))
            .pipe(switchMap(([userInfo, currentRole]) => {

                let result: ICustomMenuItem[] = [
                    {
                        order: 0, label: 'Home', icon: 'fal fa-home', iconType: 'fontawesome', iconSize: '18px', routerLink: ['/'], routerLinkActiveOptions: { exact: true }
                    },
                ];


                result.push({
                    order: 12,
                    component: LazyLoadedWidgets.REPORTS_MENU,
                    state: { scope: "GRPortalMenu" }
                });

                if (this.permissionService.hasPermission(PermissionDictionary.GR)) {
                    result.push(
                        {
                            order: 1, label: 'Dashboard', icon: 'fal fa-toolbox', iconType: 'fontawesome', iconSize: '18px', routerLink: ['/government-relations'], routerLinkActiveOptions: { exact: true }
                        },
                        {
                            order: 2, label: 'Office Follow-ups', icon: 'fal fa-buildings', iconType: 'fontawesome', iconSize: '18px',
                            component: LazyLoadedWidgets.GR_FOLOWUPS_MENU,
                        },
                        {
                            order: 3, label: 'Agent Office Follow-ups', icon: 'fal fa-buildings', iconType: 'fontawesome', iconSize: '18px',
                            component: LazyLoadedWidgets.GR_AGENT_FOLOWUPS_ITEMSMENU,
                        }
                    );
                }

                result = CustomMenuItem.orderMenuItems(result);

                return of(result);
            }));
    }



    navigateIntoMember(path: string) {
        const formattedPath = path.replace(/^[/]/g, '');

        this.router.navigate([`/member/${this.memberService.base64Sync}/${formattedPath}`]);
    }

    openLink(link: string) {
        window.open(link);
    }

    openLinkWithConfirmation(message: string, link: string) {
        if (confirm(message)) {
            this.openLink(link);
        }
    }
}
